import { CustomPrice } from '../../api.pricingCalendar';

// Shared day-of-week → raised-price resolution, and per-date effective-price resolution,
// for the calendar pages (single-apartment grid + multi-apartment timeline).

export const DOW_NAME_MAP: Record<string, number> = {
  SUNDAY: 0, MONDAY: 1, TUESDAY: 2, WEDNESDAY: 3, THURSDAY: 4, FRIDAY: 5, SATURDAY: 6,
};

export function parseDowPrices(raisedPriceJson: string | null | undefined): Record<number, number> {
  if (!raisedPriceJson) return {};
  try {
    const raw: Record<string, number> = JSON.parse(raisedPriceJson);
    const out: Record<number, number> = {};
    for (const [k, v] of Object.entries(raw)) {
      const idx = DOW_NAME_MAP[k];
      if (idx !== undefined) out[idx] = v;
    }
    return out;
  } catch {
    return {};
  }
}

export interface ResolvedDayPrice {
  price: number | null;
  promo: number | null;
  minNights: number | null;
  showDowTag: boolean;
  isBlocked: boolean;
}

/**
 * customPriceMap should be a date → CustomPrice lookup (e.g. via Object.fromEntries), not a raw array scan.
 *
 * Note: `price` still resolves via the base/DOW fallback chain even when `isBlocked` is true (the
 * backend nulls a blocked row's own price, but doesn't know about DOW/base pricing) — callers
 * MUST gate price/promo/minNights display on `isBlocked`, not on `price == null`.
 */
export function resolveDayPrice(
  customPriceMap: Record<string, CustomPrice>,
  dowPrices: Record<number, number>,
  basePricePerNight: number | null | undefined,
  dateStr: string
): ResolvedDayPrice {
  const custom = customPriceMap[dateStr] ?? null;
  const dow = new Date(dateStr + 'T00:00:00').getDay();
  const dowPrice = dowPrices[dow] ?? null;

  const price = custom?.pricePerNight ?? dowPrice ?? basePricePerNight ?? null;
  const promo = custom?.promoPrice ?? null;
  const minNights = custom?.minNights ?? null;
  const showDowTag = !custom && !!dowPrice;
  const isBlocked = custom?.isBlocked ?? false;

  return { price, promo, minNights, showDowTag, isBlocked };
}
